import { animate, state, style, transition, trigger } from '@angular/animations';
import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatIcon } from '@angular/material/icon';
import { MatMenu, MatMenuContent, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { MatToolbar } from '@angular/material/toolbar';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { TReducerState } from '../../app.config';
import { environment } from '../../core/environments/environment';
import { MonitoringService } from '../../core/error-handler/monitoring.service';
import { getApiUrl } from '../../core/interceptors/api-interceptor.interceptor';
import { HomeComponent } from '../../pages/home/home.component';
import { ItemsModalComponent } from '../../pages/items/items-modal/items-modal.component';
import { IClientInfo, IClientInfoState } from '../../state/client-info/client-info.interface';
import { UserEffects } from '../../state/user/user.effects';
import { EAuthProvider } from '../../state/user/user.interface';
import { GlobalSearchComponent } from './global-search/global-search.component';
import { ScwAppSwitcherComponent } from './scw-app-switcher/scw-app-switcher.component';

@Component({
  animations: [
    trigger('slideDownDisappear', [
      state(
        'void',
        style({
          transform: 'translateY(-100%)',
        }),
      ),
      transition('* => void', [animate('500ms ease-in-out', style({ transform: 'translateY(150%)' }))]),
    ]),
    trigger('slideDownAppear', [
      state(
        'void',
        style({
          transform: 'translateY(-100%)',
        }),
      ),
      transition('void => *', [animate('500ms ease-in-out', style({ transform: 'translateY(50%)' }))]),
    ]),
  ],
  imports: [
    MatToolbar,
    NgIf,
    MatMenuTrigger,
    MatIcon,
    TranslateModule,
    MatMenu,
    RouterLink,
    MatIconButton,
    MatButton,
    MatMenuItem,
    MatDivider,
    ScwAppSwitcherComponent,
    MatMenuContent,
    GlobalSearchComponent,
  ],
  selector: 'app-toolbar',
  styleUrl: './toolbar.component.scss',
  templateUrl: './toolbar.component.html',
})
export class ToolbarComponent implements OnInit, OnDestroy {
  public readonly version: string = environment.version;

  public fullName: string = UserEffects.getUser().name;
  public isToolAdmin: boolean = UserEffects.getUser().isAdmin;
  public isAiEnabled: boolean = UserEffects.getUser().aiEnabled;
  public authProvider: EAuthProvider = UserEffects.getUser().authProvider;
  public isDigitalFactoryAuth: boolean = UserEffects.getUser().authProvider === EAuthProvider.digitalFactory;
  public isBoardAdmin: boolean = this.getIsBoardAdmin(UserEffects.getUser().authorizations.adminBoardIds);
  public clientInfo: IClientInfo | null = null;

  private readonly subscriptions: Subscription[] = [];

  constructor(
    private readonly store: Store<TReducerState>,
    private readonly monitoringService: MonitoringService,
    private readonly router: Router,
    private readonly dialog: MatDialog,
    private readonly translate: TranslateService,
  ) {}

  public ngOnInit(): void {
    this.subscriptions.push(
      this.store.select('clientInfo').subscribe((clientInfoState: IClientInfoState) => {
        this.clientInfo = clientInfoState.clientInfo;
      }),
    );
  }

  public logout(): void {
    this.monitoringService.logoutAuthorizedUser();
    localStorage.clear();

    switch (this.authProvider) {
      case EAuthProvider.digitalFactory:
        window.location.assign(`${environment.ssoUrl}${environment.ssoLogoutUrl}`);
        break;
      case EAuthProvider.actionTracker:
        window.location.assign(
          `${getApiUrl()}/auth/logout?state=${window.location.href.replace(this.router.url, '/home')}`,
        );
        break;
    }
  }

  public openSearchDialog(): void {
    this.dialog.open(GlobalSearchComponent, {
      autoFocus: 'input',
      maxWidth: '100vw',
      panelClass: 'mobile-search-dialog',
      position: { top: '8px' },
      width: 'calc(100vw - 16px)',
    });
  }

  public openItemModal(): void {
    const addItemLabel: string = this.translate.instant('toolbar.addAction');

    HomeComponent.afterItemEditCompletedDynamic.switch(
      this.dialog
        .open(ItemsModalComponent, {
          ...ItemsModalComponent.modalParameters,
          data: {
            submitButtonText: addItemLabel,
            title: addItemLabel,
          },
        })
        .afterClosed(),
    );
  }

  private getIsBoardAdmin(adminBoardIds: -1 | number[]): boolean {
    return adminBoardIds === -1 || Boolean(adminBoardIds.length);
  }

  public ngOnDestroy(): void {
    this.subscriptions.forEach((subscription: Subscription) => subscription?.unsubscribe());
  }
}
